import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import i18n from 'i18next';
import { withTranslation } from 'react-i18next';
import { languageOptions } from '../utils/languageOptions';
import { refreshUserCache } from '../utils/refreshUserCache';
import { changeDisplayLanguage } from '../utils/displayLanguage';
import LANGUAGE_CODES from '../constants/languages';
import { TRANSLATION_FILES } from '../constants/translation';
import { flashErrorMessage } from '../actions/flash';
import { normalizeLanguage, saveLanguagePreference, subscribeLanguageSaveStatus } from '../utils/languagePreference';

export class LanguageSwitcher extends Component {
  state = { isUpdating: false, language: normalizeLanguage(i18n.language) || 'en' };

  componentDidMount() {
    i18n.on('languageChanged', this.handleI18nChange);
    this.unsubscribeSaveStatus = subscribeLanguageSaveStatus(pending => this.setState({
      saveWarning: pending
      ? this.props.t('Language save is still unconfirmed. This tab keeps your selection. Wait for the connection to recover before retrying.', { ns: TRANSLATION_FILES.translation }) : null }));
  }
  componentWillUnmount() { i18n.off('languageChanged', this.handleI18nChange); if (this.unsubscribeSaveStatus) this.unsubscribeSaveStatus(); }

  handleI18nChange = (language) => {
    this.setState({ language: normalizeLanguage(language) || 'en' });
  };

  handleLanguageChange = async (event) => {
    const language = event.target.value;
    this.setState({ isUpdating: true, saveWarning: null });
    // Saving failure must not undo the language currently being presented.
    i18n.emit('explicitLanguageSelected', language);
    changeDisplayLanguage(i18n, language);
    try {
      await saveLanguagePreference(language);
      this.setState({ retryAvailable: false });
      // book-api caches the user independently of the durable profile.
      try {
        await refreshUserCache();
      } catch (error) { Bugsnag.notifyException(error); }
    } catch (error) {
      if (!error.blocked) Bugsnag.notifyException(error);
      const message = this.props.t(error.code === 'LANGUAGE_SAVE_UNCONFIRMED'
        ? 'Language save is still unconfirmed. This tab keeps your selection. Wait for the connection to recover before retrying.' : 'Failed to update language', { ns: TRANSLATION_FILES.translation });
      this.setState({ saveWarning: error.code === 'LANGUAGE_SAVE_UNCONFIRMED' ? message : null, retryAvailable: true });
      if (!error.blocked) this.context.store.dispatch(flashErrorMessage({ message }));
    } finally {
      this.setState({ isUpdating: false });
    }
  };

  render() {
    const { t, auth } = this.props;
    const { isUpdating, language } = this.state;
    const options = languageOptions(
      LANGUAGE_CODES, auth && auth.company && auth.company.activeLanguageCodes, language,
    );
    return (
      <div className="language-switcher">
        <select onChange={this.handleLanguageChange} value={language} disabled={isUpdating}>
          {options.map(l => (
            <option key={l.value} value={l.value} disabled={l.disabled}>
              {t(l.text, { ns: TRANSLATION_FILES.constants })}
            </option>
          ))}
        </select>
        {this.state.retryAvailable && <button type="button" disabled={isUpdating} onClick={() => this.handleLanguageChange({ target: { value: language } })}>{t('Retry', { ns: TRANSLATION_FILES.translation })}</button>}
        {this.state.saveWarning && <span role="alert">{this.state.saveWarning}</span>}
        {isUpdating && <span className="spinner">⟳</span>}
      </div>
    );
  }
}
LanguageSwitcher.propTypes = {
  t: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    company: PropTypes.shape({ activeLanguageCodes: PropTypes.arrayOf(PropTypes.string) }),
  }),
};
LanguageSwitcher.defaultProps = { auth: null };
LanguageSwitcher.contextTypes = { store: PropTypes.object };
export default connect(state => ({ auth: state.auth }))(withTranslation()(LanguageSwitcher));



// WEBPACK FOOTER //
// ./src/components/LanguageSwitcher.jsx